import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import useTasks from "hooks/projects/useTasks";
import TaskFormDrawer from "../components/TaskFormDrawer";
import paths from "routes/paths";

const TaskFormPage = () => {
  const navigate = useNavigate();
  const { projectId, iterationId, taskId } = useParams();
  const isEditMode = useMemo(() => Boolean(taskId), [taskId]);
  const { fetchTasksByIteration, createTask, updateTask } = useTasks(projectId);

  const [loadingTask, setLoadingTask] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [task, setTask] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadTask = async () => {
      if (!isEditMode) {
        setLoadingTask(false);
        return;
      }

      try {
        setLoadingTask(true);
        const currentTasks =
          (await fetchTasksByIteration(projectId, iterationId)) || [];
        const foundTask = currentTasks.find((item) => String(item.id) === String(taskId));

        if (mounted) {
          setTask(foundTask || null);
        }
      } catch (error) {
        toast.error(error.message || "Failed to load task data");
      } finally {
        if (mounted) {
          setLoadingTask(false);
        }
      }
    };

    loadTask();

    return () => {
      mounted = false;
    };
  }, [
    fetchTasksByIteration,
    isEditMode,
    iterationId,
    projectId,
    taskId,
  ]);

  const handleSubmit = async (payload) => {
    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await updateTask(projectId, taskId, payload);
        toast.success("Task updated successfully");
      } else {
        await createTask(projectId, payload);
        toast.success("Task created successfully");
      }

      navigate(paths.projectIterationDetails(projectId, iterationId));
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        `Failed to ${isEditMode ? "update" : "create"} task`;
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingTask) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <TaskFormDrawer
      open
      onClose={() => navigate(-1)}
      onSubmit={handleSubmit}
      mode={isEditMode ? "edit" : "create"}
      initialValues={
        task
          ? {
              ...task,
              id: task.id,
              projectId,
              iterationId,
            }
          : null
      }
      loading={isSubmitting}
    />
  );
};

export default TaskFormPage;
